//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import angular from 'angular';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { FullscreenModal } from '@components/FullscreenModal';
import {
  FlatDefaultButton,
  PrimaryButton,
} from '@components/atoms/button/Button';
import { BodyText, SectionText } from '@components/atoms/text/Text';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { DarkGradientOverlay } from '@galleries/components/content/dark-gradient-overlay/DarkGradientOverlay';
import { InfoOverlay } from '@galleries/components/content/info-overlay/InfoOverlay';
import { MediaGrid } from '@galleries/components/content/media-grid/MediaGrid';
import SelectableImage from '@galleries/components/content/selected-image/SelectableImage';
import { LabelsSearchSection } from '@galleries/components/modals/bulk-add-labels/LabelsSearchSection';
import {
  BULK_RIGHTS_WORKFLOW_LARGE_MEDIA_WIDTH,
  BULK_RIGHTS_WORKFLOW_SMALL_MEDIA_WIDTH,
} from '@galleries/constants/galleriesConstants';
import { getContentItemImageUrl } from '@galleries/utils/getContentItemImageUrl';
import PartialCuid from '@models/PartialCuid';
import makeAngReactController from '@react-bridge/angular-react-widget';
import { getBulkActionsActions } from '@react-bridge/wrappers/ng-service-provider';
import { hasPermissionForTask } from '@services/permissions';
import { pluralize } from '@utils/stringUtils';

export const BulkActionLabelsModal = () => {
  const dispatch = useDispatch();

  // Redux state hooks
  const isOpen = useSelector(
    (state) => state.bulkActions.actionLabelsModal.isBulkAddLabelsModalOpen,
  );
  const isRemoveLabelsModal = useSelector(
    (state) => state.bulkActions.actionLabelsModal.isRemoveLabelsModal,
  );
  const items = useSelector(
    (state) => state.bulkActions.actionLabelsModal.selectedItems,
  );

  // Redux action hooks
  const {
    addLabels,
    closeAddLabelsModal,
    removeLabels,
    selectItem,
    unselectItem,
  } = getBulkActionsActions();

  // Component state hooks
  const [labels, setLabels] = useState<string[]>([]);
  const [currentlyUnselectedItems, setCurrentlyUnselectedItems] = useState<
    IContentLibraryItem[]
  >([]);

  const hasTaggingPermission = hasPermissionForTask(
    'universal_image_actions:tagging',
  );

  // Add to | Remove from
  const actionVerb = isRemoveLabelsModal ? 'Remove' : 'Add';
  const actionLocation = isRemoveLabelsModal ? 'from' : 'to';

  const addLabelTooltipContent = hasTaggingPermission
    ? `${actionVerb} ${pluralize(
        labels.length,
        'label',
      )} ${actionLocation} the selected content`
    : `You don't have permission to ${actionVerb.toLocaleLowerCase()} labels. Contact a group admin if this is a mistake.`;

  const handleItemClick = (clickedItem: IContentLibraryItem) => {
    const isCurrentlySelected = !currentlyUnselectedItems
      .map((item) => item.id)
      .includes(clickedItem.id);

    // Build the partial cuid we will use to select and unselect the item in redux
    const pcuid = PartialCuid.build({
      itemType: clickedItem.itemType,
      id: clickedItem.id,
    });

    if (isCurrentlySelected) {
      // If the item is currently selected, add it to the unselected list
      setCurrentlyUnselectedItems([...currentlyUnselectedItems, clickedItem]);
      dispatch(unselectItem(pcuid));
    } else {
      // If the item is currently unselected, remove it from the list
      const filteredUnselectedItems = currentlyUnselectedItems.filter(
        (item) => item.id !== clickedItem.id,
      );
      setCurrentlyUnselectedItems(filteredUnselectedItems);
      dispatch(selectItem(pcuid, () => null));
    }
  };

  const handleCloseModal = () => {
    // Reset the modal state
    setLabels([]);
    setCurrentlyUnselectedItems([]);

    // Close the modal
    dispatch(closeAddLabelsModal());
  };

  const handleBulkActionLabels = () => {
    if (labels.length > 0) {
      isRemoveLabelsModal
        ? dispatch(removeLabels(labels))
        : dispatch(addLabels(labels));
    }
    handleCloseModal();
  };

  const getContent = (items: IContentLibraryItem[]) => {
    const imgSize =
      items.length === 1
        ? BULK_RIGHTS_WORKFLOW_LARGE_MEDIA_WIDTH
        : BULK_RIGHTS_WORKFLOW_SMALL_MEDIA_WIDTH;

    return items.map((item) => {
      const hoverOverlay = (
        <>
          <DarkGradientOverlay />
          <InfoOverlay contentItem={item} />
        </>
      );

      return (
        <SelectableImage
          key={item.id}
          handleClick={() => handleItemClick(item)}
          hoverOverlay={hoverOverlay}
          imgSrc={getContentItemImageUrl(item, imgSize)}
          isSelected={!currentlyUnselectedItems.includes(item)}
          showBorder={true}
          showSelectableOverlay={true}
        />
      );
    });
  };

  return (
    <FullscreenModal
      headerTitle={`${actionVerb} Labels`}
      isActive={true}
      isOpen={isOpen}
      onClose={handleCloseModal}
    >
      <ContentWrapper>
        <LeftColumn>
          <MediaGrid>{getContent(items)}</MediaGrid>
        </LeftColumn>
        <RightColumn>
          <StyledSectionText>{actionVerb} Labels</StyledSectionText>
          <StyledBodyText>
            Select the labels you want to {actionVerb.toLocaleLowerCase()}{' '}
            {actionLocation.toLocaleLowerCase()} these items
          </StyledBodyText>
          <LabelsSearchSection
            isModalOpen={isOpen}
            labels={labels}
            setLabels={setLabels}
          />
          <ButtonsRow>
            <CancelButton onClick={handleCloseModal}>Cancel</CancelButton>
            <SmallTooltip
              content={addLabelTooltipContent}
              target={
                <StyledPrimaryButton
                  isDisabled={!hasTaggingPermission}
                  onClick={handleBulkActionLabels}
                >
                  {actionVerb} Labels
                </StyledPrimaryButton>
              }
            />
          </ButtonsRow>
        </RightColumn>
      </ContentWrapper>
    </FullscreenModal>
  );
};

const ButtonsRow = styled.div`
  margin-top: ${({ theme }) => theme.spacings[3]};
  display: flex;
  justify-content: flex-end;

  button {
    margin-left: ${({ theme }) => theme.spacings[2]};
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacings[4]};
  width: 100%;
`;

const LeftColumn = styled.div`
  margin-right: ${({ theme }) => theme.spacings[4]};
  width: 60%;
`;

const RightColumn = styled.div`
  width: 40%;
`;

const CancelButton = styled(FlatDefaultButton)(
  ({ theme }) => `
  position: relative;
  margin-right: ${theme.spacings[2]};
`,
);

const StyledSectionText = styled(SectionText)`
  position: relative;
`;

const StyledBodyText = styled(BodyText)(
  ({ theme }) => `
  position: relative;
  margin-top: ${theme.spacings[2]};
`,
);

const StyledPrimaryButton = styled(PrimaryButton)`
  position: relative;
`;

angular.module('curalate').component('bulkActionLabelsModal', {
  controller: makeAngReactController(<BulkActionLabelsModal />),
});
